/* eslint-disable react/jsx-no-bind */
import {
  Grid, Typography, Box, useTheme,
} from '@material-ui/core';
import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import FiltersContext from '../../contexts/FiltersContext';
import StoryContext from '../../contexts/StoryContext';
import { composeWhereClause } from '../../modules/data';
import { rdsTabulate } from '../../modules/rds';
import { stateFIPS } from '../../modules/states';
import { toPercent } from '../../modules/text';
import ResponsiveContainer from '../ResponsiveContainer';
import StateCensusTractMap from '../StateCensusTractMap';
import MapLegend from './MapLegend';
import ToggleBy from './ToggleBy';
import MessageBox from '../MessageBox';
import ToggleRaceEthnicity from './ToggleRaceEthnicity';
import { years } from '../../modules/years';

function Map() {
  const { stateCode } = useParams();
  const {
    displayBy, childRace, acceptSubsidies, qualityRating, settingType,
  } = useContext(FiltersContext);
  const { noDataColor } = useContext(StoryContext);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    const getCapacity = function getCapacity() {
      let whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]}`;
      whereClause += composeWhereClause({ settingType }, true, false); // don't use child age

      if (qualityRating.name !== 'All') {
        if (qualityRating.codeValue === 'NR') {
          whereClause += ' AND qris_participation=1 AND qris_code=NULL';
        } else if (qualityRating.codeValue === 'NP') {
          whereClause += ' AND qris_participation=0';
        } else {
          whereClause += ` AND qris_code=${qualityRating.codeValue} AND qris_participation=1`;
        }
      }

      let measure = 'capacity:SUM(capacity_tot)';
      if (displayBy === 'Providers') {
        measure = 'capacity:SUM(cnt_provider)';

        // this filter only makes sense for Providers
        if (acceptSubsidies !== 'All') {
          if (acceptSubsidies === 'Accept subsidies') {
            whereClause += ' AND is_subsidized=1';
          } else {
            whereClause += ' AND is_subsidized=0';
          }
        }
      }

      return rdsTabulate('ccdc', 'tract_licensed_capacity', {
        dims: 'tract_fips,wave_id',
        measure,
        where: whereClause,
        format: 'amcharts',
        inject: false,
      }, true, null); // use cache, unset wave
    };

    const getChildren = function getChildren() {
      const races = {
        White: 'cnt_parents_inlf_white_nh',
        Black: 'cnt_parents_inlf_black_nh',
        Hispanic: 'cnt_parents_inlf_hisp',
        Asian: 'cnt_parents_inlf_asian_nh',
        AINA: 'cnt_parents_inlf_aina_nh',
        NHPI: 'cnt_parents_inlf_nhpi_nh',
        Multiracial: 'cnt_parents_inlf_2more_nh',
        Other: 'cnt_parents_inlf_other_nh',
      };

      const whereClause = `state_fips=${stateFIPS[stateCode.toUpperCase()]}`;

      let measure = 'children:SUM(cnt_parents_inlf_total),total:SUM(cnt_parents_inlf_total)';
      if (childRace !== 'All') {
        measure = `children:SUM(${races[childRace]}),total:SUM(cnt_parents_inlf_total)`;
      }

      return rdsTabulate('3rdparty', 'acs_sams_tract_wide', {
        dims: 'census_tract',
        measure,
        where: whereClause,
        format: 'amcharts',
        inject: false,
      }, false, null); // use cache, unset wave
    };

    Promise.all([getCapacity(), getChildren()])
      .then((responses) => {
        const dataItems = {};
        const capacities = responses[0].parsedBody.dataProvider;
        const children = responses[1].parsedBody.dataProvider;

        capacities.forEach((capacity) => {
          const ci = {};
          const label = capacity.wave_id === years.rdsCovidWave.toString() ? 'from' : 'to';
          ci[label] = capacity.capacity;
          dataItems[capacity.tract_fips] = {
            ...dataItems[capacity.tract_fips], ...ci, ...{ tract_fips: capacity.tract_fips },
          };
        });

        children.forEach((child) => {
          const di = dataItems[child.census_tract];
          if (!di) {
            return;
          }
          let percentChange = 0;
          // if (_.has(di, 'from') && _.has(di, 'to')) {
          // per CCAoA, if there is no TO value in the data, assume 0
          di.to = di.to || 0;
          percentChange = (di.from - di.to) / di.from;
          // }
          dataItems[child.census_tract] = {
            ...dataItems[child.census_tract],
            ...child,
            ...{
              percentRace: child.children / child.total,
              percentChange,
            },
          };
        });

        // console.log(_.values(dataItems).filter((d) => { return !_.has(d, 'from') || !_.has(d, 'to'); }));
        // console.log(_.values(dataItems));
        setData(dataItems);
      })
      .catch((e) => {
        // console.error(e);
        setError(e);
      });
  }, [displayBy, stateCode, childRace, acceptSubsidies, qualityRating, settingType]);

  const toolTipFormatterFunction = function toolTipFormatterFunction(d) {
    if (!_.has(d, 'from') || !_.has(d, 'to') || (childRace !== 'All' && !_.has(d, 'percentRace'))) {
      return 'Insufficient sample size';
    }

    let result = `In tract <b>${d.id}</b>`;

    if (childRace !== 'All') {
      result += `, where <b>${toPercent(d.percentRace, 2)}</b> of children are <b>${childRace}</b>,`;
    }

    function showQualityRating() {
      let label = ` with quality rating of <b>${qualityRating.name.toLowerCase().replace('c2q', 'C2Q')}</b>`;
      if (qualityRating.codeValue === 'NP') {
        label = `<b> ${qualityRating.name.toLowerCase()}</b>`; // not in quality rating program
      }

      if (qualityRating.codeValue === 'NR') {
        label = '<b> with no quality rating reported</b>'; // not in quality rating program
      }

      return qualityRating.name !== 'All' ? label : '';
    }

    if (d.from - d.to === 0) {
      result += ` there has been no change in the number of <b>${settingType !== 'All' ? settingType.toLowerCase().replace('fcc', 'FCC') : ''}</b> <b>${displayBy.toLowerCase()}</b>${showQualityRating()}${acceptSubsidies !== 'All' ? ` that <b>${acceptSubsidies.toLowerCase()}</b>` : ''}.`;
    } else {
      result += ` there ${Math.abs(d.from - d.to) < 2 ? 'was' : 'were'} <b>${Math.abs(d.from - d.to)}</b> ${d.from - d.to > 0 ? 'fewer' : 'more'} 
      <b>${settingType !== 'All' ? settingType.toLowerCase().replace('fcc', 'FCC') : ''}</b> 
      <b>${displayBy.toLowerCase()}</b>${showQualityRating()}${acceptSubsidies !== 'All' ? ` that <b>${acceptSubsidies.toLowerCase()}</b>` : ''}. 
    That is a <b>${toPercent(Math.abs(d.percentChange || (d.from - d.to) / d.from))}</b> ${d.from - d.to > 0 ? 'decrease' : 'increase'} from ${years.covidFromMonthYear} to ${years.covidToMonthYear}. 
    ${d.from - d.to < 0 ? 'Increase could be a result of changing needs based on COVID-19 or a lag in data.' : ''}
    `;
    }
    return result;
  };

  return (
    <Grid
      spacing={2}
      container
      direction="row"
      justify="center"
    >
      <Grid item xs={12} sm={9}>
        {error && <Typography variant="body1" align="center" color="error">{error.message}</Typography>}
        <Box displayPrint="none" display={{ xs: 'block', sm: 'none', md: 'none' }}>
          <ResponsiveContainer height={200} width="60%">
            <MapLegend noDataColor={noDataColor} />
          </ResponsiveContainer>
        </Box>

        <ResponsiveContainer width="100%" aspect={1.3}>
          <StateCensusTractMap
            stateCode={stateCode}
            toolTipFormatterFunction={toolTipFormatterFunction}
            data={data}
            colorRowsToUse={childRace === 'All' ? 0 : 3}
            noDataColor={noDataColor}
          />
        </ResponsiveContainer>

      </Grid>
      <Grid item xs={12} sm={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box display={{ xs: 'none', sm: 'block', md: 'block' }}>
              <ResponsiveContainer height={200}>
                <MapLegend noDataColor={noDataColor} />
              </ResponsiveContainer>
            </Box>
          </Grid>
          <Grid item xs={12} style={{ paddingLeft: theme.spacing(6) }}>
            <ToggleBy />
          </Grid>
          <Grid item xs={12} style={{ paddingLeft: theme.spacing(6) }}>
            <ToggleRaceEthnicity />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={10} sm={7}>
        <MessageBox>
          <Typography variant="body1" align="left">
            The map above shows the percentage change in child care providers or slots by census tract from {years.covidFromMonthYear} to {years.covidToMonthYear}. The filters can be applied to display setting type, quality rating, and/or subsidy acceptance. You can also review the % of children within that tract by race/ethnicity to further explore the implications.
          </Typography>
        </MessageBox>
      </Grid>
    </Grid>
  );
}

export default Map;
